import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer as MuiTableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  Menu,
  MenuItem,
  debounce,
} from '@mui/material';
import { MoreHoriz, Search } from '@mui/icons-material';

import { groupsAtom } from '@/interfaces/site';
import { defaultGroupIdAtom, deleteGroupAtom, selectedGroupIdAtom } from '@/interfaces/group';
import AddGroupDialog from '@/components/setting/groups/AddGroupDialog';
import EditGroupNameDialog from '@/components/setting/groups/EditGroupNameDialog';

import type { ChangeEvent, MouseEvent } from 'react';
import type { GroupData } from '@/@types/site';

const TableContainer = styled(MuiTableContainer)`
  background-color: white;
  & tbody td {
    padding: 8px 16px;
  }
`;

export default function GroupListPage() {
  const { isPending, data: groups, refetch } = useAtomValue(groupsAtom);
  const { mutate: deleteGroup, data: deleteSuccess } = useAtomValue(deleteGroupAtom);
  const setSelectedGroupId = useSetAtom(selectedGroupIdAtom);
  const setDefaultGroupId = useSetAtom(defaultGroupIdAtom);

  const [selectedGroup, setSelectedGroup] = useState<GroupData>();
  const [query, setQuery] = useState('');
  const [tableData, setTableData] = useState<GroupData[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [dialog, setDialog] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (isPending) return;
    setTableData(groups);
    setQuery('');
    setDefaultGroupId(groups.find((group) => group.isDefault).id);
  }, [groups, isPending, setDefaultGroupId]);
  useEffect(() => {
    if (deleteSuccess === undefined) return;
    if (deleteSuccess) {
      window.alert(t('Settings.message.deleteGroupSuccess'));
      refetch();
    } else {
      window.alert(t('Settings.message.deleteGroupFailed'));
    }
  }, [deleteSuccess, refetch, t]);

  function onAddGroupClick() {
    setDialog('ADD_GROUP');
  }
  function onActionClick(e: MouseEvent<HTMLButtonElement>, group: GroupData) {
    setAnchorEl(e.currentTarget);
    setSelectedGroup(group);
    setSelectedGroupId(group.id);
  }
  function onRenameGroupClick() {
    setAnchorEl(null);
    setDialog('RENAME_GROUP');
  }
  function onDeleteGroupClick() {
    const ok = window.confirm(
      `${t('Common.message.confirmDelete')}\n${t('Settings.label.groupName')}: ${selectedGroup.name}`,
    );
    if (ok) {
      setAnchorEl(null);
      deleteGroup(selectedGroup.id);
    }
  }
  const onDialogClose = useCallback(() => {
    setDialog('');
    refetch();
  }, [refetch]);
  const filterGroups = debounce((query: string) => {
    if (query === '') {
      setTableData(groups);
    } else {
      setTableData(groups.filter((group) => group.name.includes(query)));
    }
  }, 500);
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    filterGroups(value);
    setQuery(value);
  }

  return (
    <>
      <Stack gap={3}>
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, bgcolor: 'white' }}>
          <TextField
            value={query}
            onChange={onQueryChange}
            placeholder={t('Settings.label.searchGroup')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '300px', mr: 'auto' }}
          />
          <Button variant="contained" onClick={onAddGroupClick}>
            {t('Settings.label.addGroup')}
          </Button>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('Common.label.name')}</TableCell>
                <TableCell>{t('Common.label.devices')}</TableCell>
                <TableCell>{t('Common.label.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isPending &&
                tableData.map((group) => {
                  return (
                    <TableRow key={group.id}>
                      <TableCell>
                        <Link to={`/setting/groups?id=${group.id}`}>{group.name}</Link>
                      </TableCell>
                      <TableCell>{group.deviceCount}</TableCell>
                      <TableCell>
                        <IconButton sx={{ p: 1 }} onClick={(e) => onActionClick(e, group)}>
                          <MoreHoriz fontSize="medium" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <MenuItem onClick={onRenameGroupClick}>{t('Settings.label.renameGroup')}</MenuItem>
                <MenuItem onClick={onDeleteGroupClick}>{t('Settings.label.deleteGroup')}</MenuItem>
              </Menu>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog open={dialog === 'ADD_GROUP'} onClose={onDialogClose}>
        <AddGroupDialog onClose={onDialogClose} />
      </Dialog>
      <Dialog open={dialog === 'RENAME_GROUP'} onClose={onDialogClose}>
        <EditGroupNameDialog groupName={selectedGroup?.name} onClose={onDialogClose} />
      </Dialog>
    </>
  );
}
